import React from 'react';
import { Modal, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './CustomModal.css';

/**
 * CustomModal - A highly reusable modal component built on Ant Design
 * Matches MUI modal functionality with proper viewport handling and responsiveness
 */
const CustomModal = ({
  isOpen,
  onClose,
  title = 'Modal Title',
  subtitle,
  children,
  primaryActionText = 'Continue',
  onPrimaryAction,
  secondaryActionText = 'Cancel',
  onSecondaryAction,
  hideSecondaryAction = false,
  customActions,
  width = 600,
  height, // can be number, string, or undefined
  rightOffset = 1,
  primaryActionDisabled = false,
  loading = false,
  noActionButton = false,
  isCenter = false,
  confirm = null,
  error = false,
  primaryButtonType = 'primary',
  primaryButtonColor = null,
  maskClosable = false,
  destroyOnClose = true,
  className = '',
  style = {},
  overflow = 'auto',
  backdropColor = 'rgba(0, 0, 0, 0.5)', // Neutral backdrop - customize to match your theme
  margin = '20px', // Match MUI margin
}) => {
  const isCaution = confirm?.type === 'caution';

  const getTitleColor = () => {
    if (!confirm) return 'text-gray-900';
    switch (confirm.type) {
      case 'caution':
        return 'text-red-600';
      case 'success':
        return 'text-green-600';
      default:
        return 'text-gray-900';
    }
  };

  // Modal wrapper positioning - handles the side offset
  const modalStyle = isCenter
    ? {
      top: 0,
      paddingBottom: 0,
      margin: margin,
      borderRadius: '20px',
      overflow: 'hidden',
    }
    : {
      position: 'fixed',
      right: `${rightOffset || -20}px`, // Add 20px spacing from right edge
      top: '20px', // Space from top
      bottom: '20px', // Space from bottom
      height: 'auto', // Auto height with top/bottom spacing
      maxHeight: 'calc(100vh - 40px)', // Account for top + bottom margin
      margin: 0,
      padding: 0,
      paddingBottom: 0,
    };

  // Modal content/paper styling
  const contentStyle = isCenter
    ? {
      borderRadius: '20px',
      overflow: 'hidden',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '5px 20px 40px 0px rgba(37, 40, 43, 0.08)',
      border: '0.5px solid #e5e7eb',
      borderTopLeftRadius: '20px',
      borderTopRightRadius: '20px',
      borderBottomLeftRadius: '20px',
      borderBottomRightRadius: '20px',
    }
    : {
      borderRadius: '20px', // Full border radius on all corners!
      borderTopLeftRadius: '20px',
      borderBottomLeftRadius: '20px',
      borderTopRightRadius: '20px',
      borderBottomRightRadius: '20px',
      height: '100%',
      maxHeight: 'calc(100vh - 40px)', // Account for top + bottom margin
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '5px 20px 40px 0px rgba(37, 40, 43, 0.08)',
      border: '0.5px solid #e5e7eb',
    };

  // Modal body styling - handles internal scrolling
  const bodyStyle = {
    padding: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    ...(isCenter && {
      borderRadius: '20px',
    }),
  };

  // Calculate content max height
  const getContentMaxHeight = () => {
    if (isCenter) {
      return 'calc(90vh - 140px)'; // Account for header and footer
    }
    if (height) {
      const heightValue = typeof height === 'number' ? `${height}px` : height;
      return `calc(${heightValue} - 140px)`;
    }
    return 'calc(100vh - 140px)';
  };

  // Determine button styles based on state and type
  // Determine button styles based on state and type
  const getButtonClassName = () => {
    const baseClasses = 'px-6 h-10 rounded-lg transition-colors font-medium custom-primary-action-btn';

    // Don't apply color classes if using primaryButtonColor - we'll use inline styles instead
    if (primaryButtonColor) {
      return baseClasses;
    }

    if (error) {
      return `${baseClasses} bg-red-500 hover:bg-red-600 border-red-500 text-white`;
    }

    if (confirm && isCaution) {
      return `${baseClasses} bg-red-500 hover:bg-red-600 border-red-500 text-white`;
    }

    if (confirm && !isCaution) {
      return `${baseClasses} bg-green-500 hover:bg-green-600 border-green-500 text-white`;
    }

    // Default orange button
    return `${baseClasses} bg-orange-500 hover:bg-orange-600 border-orange-500 text-white`;
  };

  // Add this helper function to get button inline styles
  const getButtonStyle = () => {
    if (!primaryButtonColor) return { minWidth: '120px' };

    // Map color names to hex values
    const colorMap = {
      orange: { bg: '#fb923c', hover: '#f97316', border: '#fb923c' },
      blue: { bg: '#3b82f6', hover: '#2563eb', border: '#3b82f6' },
      green: { bg: '#22c55e', hover: '#16a34a', border: '#22c55e' },
      red: { bg: '#ef4444', hover: '#dc2626', border: '#ef4444' },
      gray: { bg: '#6b7280', hover: '#4b5563', border: '#6b7280' },
      grey: { bg: '#6b7280', hover: '#4b5563', border: '#6b7280' },
      purple: { bg: '#a855f7', hover: '#9333ea', border: '#a855f7' },
      pink: { bg: '#ec4899', hover: '#db2777', border: '#ec4899' },
      yellow: { bg: '#eab308', hover: '#ca8a04', border: '#eab308' },
      indigo: { bg: '#6366f1', hover: '#4f46e5', border: '#6366f1' },
    };

    const colors = colorMap[primaryButtonColor.toLowerCase()] || colorMap.orange;

    return {
      minWidth: '120px',
      backgroundColor: colors.bg,
      borderColor: colors.border,
      color: '#fff',
    };
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={null}
      footer={null}
      width={width}
      style={{ ...modalStyle, ...style }}
      className={`custom-modal ${isCenter ? 'modal-center' : 'modal-right'} ${className}`}
      closeIcon={null}
      centered={isCenter}
      maskClosable={maskClosable}
      destroyOnClose={destroyOnClose}
      transitionName={isCenter ? 'ant-fade' : 'ant-slide-left'}
      maskTransitionName="ant-fade"
      styles={{
        body: bodyStyle,
        content: contentStyle,
        wrapper: !isCenter
          ? {
            position: 'fixed',
            right: `${rightOffset + 20}px`, // Add 20px spacing from right edge
            top: '20px',
            bottom: '20px',
            height: 'auto',
            maxHeight: 'calc(100vh - 40px)',
            width: typeof width === 'number' ? `${width}px` : width,
            pointerEvents: 'auto',
          }
          : {},
        mask: {
          backgroundColor: backdropColor,
        },
      }}
      wrapClassName={!isCenter ? 'modal-right-wrapper' : ''}
    >
      {/* Header */}
      <div className={`bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0 rounded-t-[20px]`}>
        <div className="flex justify-between items-start">
          <div className="flex-1 pr-8">
            <h2 className={`text-lg font-semibold mb-1 ${getTitleColor()}`}>
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
            )}
          </div>
          {!confirm && (
            <button
              onClick={onClose}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <CloseOutlined className="text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{
          maxHeight: getContentMaxHeight(),
          overflowY: overflow,
        }}
      >
        {confirm ? (
          <div className="px-6 py-6">
            <p className="text-gray-700 text-sm leading-relaxed">{confirm.message}</p>
          </div>
        ) : (
          <div className={noActionButton ? 'pb-5' : ''}>{children}</div>
        )}
      </div>

      {/* Footer */}
      {!noActionButton && (
        <div className="flex justify-end items-center gap-3 px-8 py-6 bg-white border-t border-gray-200 flex-shrink-0 rounded-b-[20px]">
          {customActions || (
            <>
              {!hideSecondaryAction && (
                <Button
                  onClick={onSecondaryAction || onClose}
                  className="px-6 h-10 text-gray-600 hover:text-gray-800 transition-colors border-gray-300 font-medium"
                  size="large"
                >
                  {secondaryActionText}
                </Button>
              )}
              <Button
                type={primaryButtonType}
                onClick={onPrimaryAction}
                disabled={primaryActionDisabled}
                loading={loading}
                size="large"
                className={getButtonClassName()}
                style={getButtonStyle()}
              >
                {primaryActionText}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Global styles for proper modal positioning and button states */}
      <style jsx>{`
        /* Disabled button styles */
        :global(.custom-primary-action-btn[disabled]),
        :global(.custom-primary-action-btn.ant-btn[disabled]),
        :global(.custom-primary-action-btn.ant-btn-disabled) {
          background-color: #bdbdbd !important;
          color: #444 !important;
          border-color: #bdbdbd !important;
          opacity: 1 !important;
          cursor: not-allowed !important;
        }

        :global(.custom-primary-action-btn[disabled]:hover),
        :global(.custom-primary-action-btn.ant-btn[disabled]:hover),
        :global(.custom-primary-action-btn.ant-btn-disabled:hover) {
          background-color: #bdbdbd !important;
          color: #444 !important;
          border-color: #bdbdbd !important;
        }

        /* Enabled button styles - Orange (default) */
        :global(.custom-primary-action-btn:not([disabled])) {
          background-color: #fb923c !important; /* orange-500 */
          color: #fff !important;
          border-color: #fb923c !important;
        }

        :global(.custom-primary-action-btn:not([disabled]):hover),
        :global(.custom-primary-action-btn:not([disabled]):focus) {
          background-color: #f97316 !important; /* orange-600 */
          color: #fff !important;
          border-color: #f97316 !important;
        }

        /* Override Ant Design's default blue hover */
        :global(.ant-btn-primary:not([disabled]):hover) {
          background-color: #f97316 !important;
          border-color: #f97316 !important;
        }

        /* Disabled text color override */
        :global(.ant-btn:disabled > *),
        :global(.ant-btn[disabled] > *) {
          color: #444 !important;
        }

        /* Modal positioning */
        :global(.modal-right-wrapper) {
          position: fixed !important;
          right: ${rightOffset + 20}px !important;
          top: 20px !important;
          bottom: 20px !important;
          left: auto !important;
          height: auto !important;
          max-height: calc(100vh - 40px) !important;
          width: ${typeof width === 'number' ? `${width}px` : width} !important;
          pointer-events: none !important;
        }

        :global(.modal-right-wrapper .ant-modal) {
          position: relative !important;
          height: 100% !important;
          max-height: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
          pointer-events: auto !important;
        }

        :global(.modal-right-wrapper .ant-modal-content) {
          position: relative !important;
          height: 100% !important;
          max-height: 100% !important;
          border-radius: 20px !important;
          display: flex !important;
          flex-direction: column !important;
        }

        :global(.modal-center .ant-modal-content) {
          max-height: 90vh !important;
          display: flex !important;
          flex-direction: column !important;
        }

        /* Ensure smooth transitions */
        :global(.ant-modal-wrap) {
          transition: all 0.3s ease !important;
        }

        /* Handle mask for this specific modal */
        :global(.custom-modal ~ .ant-modal-mask) {
          background-color: ${backdropColor} !important;
        }
      `}</style>
    </Modal>
  );
};

export default CustomModal;